import { useState, useCallback } from "react";

import { Button, Card, Icon } from "@aidron/aidron-ds";

import { i18n } from "../../../translate/i18n";

import {
  SaleIcon,
  StockIcon,
} from "./ListItem.style";

import PropTypes from 'prop-types';

import {
  ButtonWithModal,
  ProductHierarchyHeading,
  TextAndLabelView,
  ModalTemplate,
} from "../sharedComponents.index";
import { SYSTEM_COLORS } from '../../system.colors'
import { ActionModal, OpportunityActionModal } from "../ActionModal/ActionModal";

import { CustomText, CustomCard } from "../../StyledComponents/styleComponents.index";

import { OpportunityAccordion } from "./components/listItem.index"

import { currencyFormatter, createPercentFormatter, textFormatter } from "../../../utils/formatter/formatter";

const { t } = i18n;

const TYPE_DETAILS_STATIC = {
  additionalSale: {
    title: t("additionalSale"),
    summary: t("additionalSaleExplanation"),
    percentLabel: t("expectedIncreaseInSales"),
    icon: <SaleIcon />,
  },
  avoidLoss: {
    title: t("avoidSalesLoss"),
    summary: t("avoidSalesLossExplanation"),
    percentLabel: t("expectedIncreaseInSales"),
    icon: <SaleIcon />,
  },
  stock: {
    title: t("optimizePurchase"),
    summary: t("optimizePurchaseExplanation"),
    percentLabel: t("futurePurchaseReduction"),
    icon: <StockIcon />,
  },
  trend: {
    title: "Reversão de Tendência",
    summary: "Desempenho de venda",
    percentLabel: "Reverter a venda",
    icon: <StockIcon />,
  },  
};

const TYPE_SUBTITLE = {
  ["Otimização de Compra"]: "Ajuste no plano",
  ["Reversão de Tendência"]: "Desempenho de Venda"
}

const getTypeDetails = (type1, textPt, textEn, language) => {
  if (type1 === "optimizeBuy") {
    return {
      title: t("buyOptimization"),
      summary: language === "pt-Br" ? textPt : textEn,
      percentLabel: t("futurePurchaseReduction"),
      icon: <SaleIcon />,
    };
  }
  return TYPE_DETAILS_STATIC[type1];
};

const STATUS_DETAILS = new Map([
  ["active", t("active")],
  ["reactivated", t("reactivated")],
  ["checkout", t("checkout")],
]);

const CardOutlined = ({ children }) => { 
  return (
    <div style={{
      boxShadow: `0 4px 8px ${SYSTEM_COLORS.BLACK.BLACK_4_PERCENT}`,
      borderRadius:  '4px',
      padding: '16px',
      border: `1px solid ${SYSTEM_COLORS.GRAY.COOL_LIGHT_GRAY}`,
    }}>
      {children}
    </div>
  );
};

const getTrendDetails = ({ ctx }) => {
  
  const { direction } = ctx || {};
  
  if (!direction) {
    return {
      text: "Neutro",
      color: SYSTEM_COLORS.GRAY.ZINC_700,
      bg: SYSTEM_COLORS.GRAY.CULTURED,
      isUp: null,
    };
  }

  if (direction.toLowerCase() === "alta") {
    return {
      text: "Alta",
      color: SYSTEM_COLORS.GREEN.FOREST_GREEN,
      bg: SYSTEM_COLORS.GREEN.FOREST_GREEN_10,
      isUp: true,
    };
  }

  if (direction.toLowerCase() === "queda") {
    return {
      text: "Queda",
      color: SYSTEM_COLORS.RED.BRICK_RED,
      bg: SYSTEM_COLORS.RED.BRICK_RED_10,
      isUp: false,
    };
  }

  return {
    text: "Neutro",
    color: SYSTEM_COLORS.GRAY.ZINC_700,
    bg: SYSTEM_COLORS.GRAY.CULTURED,
    isUp: null,
  };
};

const TrendPill = ({ ctx }) => {
  const { trendStatus } = ctx || {};

  const { color, bg, text, isUp } = getTrendDetails({
    ctx: { direction: trendStatus },
  });

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        color,
        backgroundColor: bg,
        border: `1px solid ${color}`,
        whiteSpace: "nowrap",
      }}
    >
      <svg
        width={11}
        height={11}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {isUp === true ? (
          <>
            <path d="M16 7h6v6" />
            <path d="m22 7-8.5 8.5-5-5L2 17" />
          </>
        ) : isUp === false ? (
          <>
            <path d="M16 17h6v-6" />
            <path d="m22 17-8.5-8.5-5 5L2 7" />
          </>
        ) : (
          <path d="M5 12h14" />
        )}
      </svg>

      {text}
    </span>
  );
};

export const generateSubtitlePeriod = ({ startDate, endDate, mode = 'abbr' }) => {
  const MONTH_MAP = {
    1: "Jan", 2: "Fev", 3: "Mar", 4: "Abr", 5: "Mai", 6: "Jun",
    7: "Jul", 8: "Ago", 9: "Set", 10: "Out", 11: "Nov", 12: "Dez"
  }

  const MONTH_MAP_FULL_NAME = {
    1: "Janeiro", 2: "Fevereiro", 3: "Março", 4: "Abril", 5: "Maio", 6: "Junho",
    7: "Julho", 8: "Agosto", 9: "Setembro", 10: "Outubro", 11: "Novembro", 12: "Dezembro"
  }

  if(mode !== 'abbr') {
    const initialPeriod = startDate.split("T")[0].split("-")[1];
    const finalPeriod = endDate.split("T")[0].split("-")[1];

    const initialYear = startDate.split("T")[0];
    const finalYear = endDate.split("T")[0];

    return `${MONTH_MAP_FULL_NAME[Number(initialPeriod)]}/${initialYear} - ${MONTH_MAP_FULL_NAME[Number(finalPeriod)]}/${finalYear}`;
  }

  const initialPeriod = startDate.split("T")[0].split("-")[1];
  const finalPeriod = endDate.split("T")[0].split("-")[1];

  const initialYear = startDate.split("T")[0].split("-")[0].slice(-2);
  const finalYear = endDate.split("T")[0].split("-")[0].slice(-2);

  return `${MONTH_MAP[Number(initialPeriod)]}/${initialYear} - ${MONTH_MAP[Number(finalPeriod)]}/${finalYear}`;
};

export const ListItem = ({ actions, ctx, uiState, onItemClick = () => {} }) => {

  const { 
    refetchOpportunities = () => {}, 
    handleRemoveOpportunity = () => {},
    resetModalState = () => {},
    setSelectedId = () => {},
    setRemovalReason = () => {},
    resetOppError = () => {}
  } = actions || {};

  const { 
    opportunity, 
    activeOppsWithTypes, 
    selectedId, 
    predictions = [],
    options,
    removalReason,
    products = []
  } = ctx || {};

  const { 
    accordionButton = false, 
    reactivateOpportunity = false,
    isCheckoutPage = false,
    accordionMode = "default",
    page = null,
    delimiter = "|",
    updateOppError = null,
    CardSection = ListItem.CardSection,
    cardSectionCtx = {},
    cardSectionActions = {},
    cardSectionUiState = {},
  } = uiState || {};
  
  const { 
    opportunityId, productId, type1, textPt, trendStatus,
    textEn, value, percent, status, removedDate,
  } = opportunity;

  const [openAccordion, setOpenAccordion] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [openOpportunityActionModal, setOpenOpportunityActionModal] = useState(false);

  const language = localStorage.getItem("i18nextLng");

  const getOpportunityDetails = (type1) => getTypeDetails(type1, textPt, textEn, language);
  const opportunityDetails = getOpportunityDetails(type1);

  const statusText = STATUS_DETAILS.get(status);

  let removedDateText = null;
  if (removedDate) {
    const parsedDate = new Date(removedDate);
    if (!isNaN(parsedDate)) removedDateText = new Intl.DateTimeFormat(language).format(parsedDate);
  }

  const renderActionModal = useCallback(
    ({ closeModal }) => (
      <ActionModal 
        actions={{
          refetchOpportunities,
          handleRemoveOpportunity,
          setSelectedId,
          setRemovalReason
        }}
        ctx={{
          opportunityId,
          status,
          options,
          selectedId,
          removalReason,
        }}
      />
    ),
    [
      opportunityId, 
      status, 
      options, 
      selectedId, 
      removalReason,  
      handleRemoveOpportunity, 
      setSelectedId, 
      setRemovalReason,
      refetchOpportunities
    ]
  );

  const renderReactivateModal = useCallback(
    ({ closeModal }) => (
      <>
        <CustomText margin="0 0 1.5rem 0">{t("willBeListedAsOpportunity")}</CustomText>
        <div style={{ display: 'flex', justifyContent: 'end', gap: '20px' }}> 
          <Button label="Cancelar" type="outlined" onClick={closeModal} />
          <Button label="Ok" onClick={() => { reactivateOpportunity(opportunityId); closeModal(); }} />
        </div>
      </>
    ),
    [reactivateOpportunity, opportunityId]
  ); 

  const handleAccordionButton = () => setOpenAccordion(!openAccordion);

  const onButtonClick = (e) => {
    e.stopPropagation();
    setOpenModal(true);
  };

  const isDetailed = page === 'detailed_opportunities';

  const baseCardSectionCtx = {
    opportunity: opportunity,
    opportunities: activeOppsWithTypes,
    getOpportunityDetails: getOpportunityDetails,
    predictions: predictions,
    products: products,
  };

  if (isCheckoutPage) {
    return (
      <>
        <CustomCard
          onClick={() => onItemClick(opportunityId)}
          cursor="pointer"
          margin="0 0 20px 0"
          backgroundColor={opportunityId === selectedId ? "transparentDarkOrange" : ""}
          greyBackgroundOnHover>
          <div 
            style={{
              display: 'grid',
              gridTemplateColumns: '30% repeat(3, 1fr) 10%',
              blockSize: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative'
            }}>
            <div
              style={{
                padding: '0 10px',
                textAlign: 'default',
                borderInlineEnd: `1px solid ${SYSTEM_COLORS?.GRAY?.SPANISH_GRAY}`,
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '40px',
                alignItems: 'center'
              }}>
              {opportunityDetails.icon}
              <ProductHierarchyHeading ctx={{ products: products, productId: productId }} uiState={{ delimiter: delimiter }} />
            </div>

            <div 
              style={{
                padding: '0 20px',
                textAlign: 'center',
                borderInlineEnd: `1px solid ${SYSTEM_COLORS?.GRAY?.SPANISH_GRAY}`,
                blockSize: '80px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
              <TextAndLabelView label={t("opportunity")} text={opportunityDetails.title} />
            </div>

            <div 
              style={{
                padding: '0 20px',
                textAlign: 'center',
                borderInlineEnd: "none",
                blockSize: '80px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'                
              }}>
              <TextAndLabelView label={t("opportunitySize")} heading={currencyFormatter.format(value)} />
            </div>

            <div
              style={{
                padding: '0 20px',
                textAlign: 'center',
                borderInlineEnd: "none",
                blockSize: '80px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'end',
                cursor: 'pointer'
              }}>
              <Button icon="x" type="outlined" onClick={e => onButtonClick(e)} />
            </div>
          </div>
        </CustomCard>

        <ModalTemplate open={openModal} width="650px" closingFunction={() => setOpenModal(false)} heading=" ">
          {openModal && (
            <ActionModal 
              actions={{
                refetchOpportunities,
                handleRemoveOpportunity,
                setSelectedId,
                setRemovalReason
              }}
              ctx={{
                opportunityId,
                status,
                options,
                selectedId,
                removalReason,
              }}
            />
          )}
        </ModalTemplate>
      </>
    );    
  };

  if (isDetailed) {
    
    const { text } = getTrendDetails({
      ctx: { direction: trendStatus },
    });

    return (
      <div style={{
        border: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`, borderRadius: 30, overflow: "visible", boxShadow: `0 4px 30px ${SYSTEM_COLORS.BLACK.BLACK_8_PERCENT}`,
        background: SYSTEM_COLORS.WHITE.WHITE_SMOKE, transition: "border-color 0.3s ease" }}>
        <div style={{
          display: "grid", gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr",
          alignItems: "center", padding: "14px 12px", gap: 8 }}>
          <div>
            <hgroup style={{ color: SYSTEM_COLORS.BLACK.EERIE_BLACK, marginBottom: 4 }}>
              <ProductHierarchyHeading 
                ctx={{ products: products, productId: productId }} 
                uiState={{ delimiter: delimiter, page: "detailed_opportunities" }} />
            </hgroup>
          </div>

          <hgroup style={{ textAlign: "center" }}>
            <p style={{ fontSize: "0.875rem", fontWeight: 600, color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
              {opportunityDetails.title}
            </p>
            <span style={{ fontSize: "0.688rem", color: SYSTEM_COLORS.GRAY.DIM_GRAY }}>{TYPE_SUBTITLE[opportunityDetails.title]}</span>
          </hgroup>

          <hgroup style={{ textAlign: "center" }}>
            <p style={{ fontSize: "0.875rem", fontWeight: 800, color: SYSTEM_COLORS.ORANGE.BURNT_ORANGE }}>
              {currencyFormatter.format(value)}
            </p>
            <span style={{ fontSize: "0.688rem", color: SYSTEM_COLORS.GRAY.DIM_GRAY }}>{
              generateSubtitlePeriod({
                startDate: opportunity?.startDate,
                endDate: opportunity?.endDate
              })
            }</span>
          </hgroup>

          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center", textAlign: "center" }}>
            <TrendPill ctx={{ trendStatus }} />
            <span style={{ fontSize: "0.688rem", color: SYSTEM_COLORS.GRAY.DIM_GRAY }}>
              {textFormatter(text)}
            </span>
          </div>
          
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {reactivateOpportunity ? (
              <Button
                label={`${t("actions")}...`}
                type="outlined"
                onClick={() => {
                  resetModalState();
                  resetOppError();
                  setOpenOpportunityActionModal(true);
                }}
              />
            ) : (
              <ButtonWithModal
                ctx={{
                  label: t("reopen"),
                  modalHeading: t("wishReopenOpportunity"),
                  icon: "",
                  modalWidth: "650px",
                  onOpen: resetModalState,
                  renderModalContent: renderReactivateModal
                }}
              />
            )}
          </div>

        </div>

        {openAccordion && (
          <div style={{ padding: "12px", borderTop: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}` }}>
            <CardSection
              ctx={{ ...baseCardSectionCtx, ...cardSectionCtx }}
              actions={{ ...cardSectionActions }}
              uiState={{ accordionMode, ...cardSectionUiState }}
            />
          </div>
        )}

        {accordionButton && (
          <div style={{ display: "flex", justifyContent: "center", borderTop: `2px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`, padding: "8px 0" }}>
            <button
              onClick={handleAccordionButton}
              aria-label="Expandir"
              style={{
                width: 32, height: 32, borderRadius: "50%", border: "none", background: SYSTEM_COLORS.RED.CORAL_RED,
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                boxShadow: `0 1px 2px ${SYSTEM_COLORS.BLACK.BLACK_10_PERCENT}`
              }}
            >
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.WHITE.WHITE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: openAccordion ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          </div>
        )}

        <OpportunityActionModal 
          actions={{
            refetchOpportunities,
            handleRemoveOpportunity,
            setSelectedId,
            setRemovalReason,
            resetOppError,
            onClose: () => setOpenOpportunityActionModal(false),
          }}
          ctx={{
            opportunityId,
            status,
            options,
            selectedId,
            removalReason,
            productId,
            products,
          }}
          uiState={{ isOpen: openOpportunityActionModal, updateOppError }}
        />
      </div>
    );
  }

  return (
    <Card>
      <div 
        style={{
          display: 'grid',
          gridTemplateColumns: '30% 1fr 0.7fr 1fr 0.5fr 0.5fr',
          blockSize: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}>
        <div 
          style={{
            padding: '0 10px',
            textAlign: 'default',
            borderInlineEnd: `1px solid ${SYSTEM_COLORS.GRAY.SPANISH_GRAY}`,
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '40px',
            alignItems: 'center'
          }}>
          {opportunityDetails.icon}
          <ProductHierarchyHeading ctx={{ products: products, productId: productId }} uiState={{ delimiter: delimiter }} />
        </div>

        <div 
          style={{
            padding: '0 20px',
            textAlign: 'center',
            blockSize: '80px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            borderInlineEnd: `1px solid ${SYSTEM_COLORS.GRAY.SPANISH_GRAY}`,
            alignItems: 'center'
          }}>
          <TextAndLabelView label={t("opportunity")} labelSize="xs" text={opportunityDetails.title} />
        </div>

        <div 
          style={{
            padding: '0 20px',
            textAlign: 'center',
            blockSize: '80px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            borderInlineEnd: `1px solid ${SYSTEM_COLORS.GRAY.SPANISH_GRAY}`,  
            alignItems: 'center',
          }}>
          <TextAndLabelView
            label={t("percent")}
            labelSize="xs"
            heading={`${percent > 0 ? "↑" : "↓"} ${createPercentFormatter({ 
              minimumFractionDigits: 0, 
              maximumFractionDigits: 0 }).format(percent / 100)}`}
            contentSize="md"/>
        </div>
        
        <div 
          style={{
            padding: '0 20px',
            textAlign: 'center',
            blockSize: '80px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            borderInlineEnd: `1px solid ${SYSTEM_COLORS.GRAY.SPANISH_GRAY}`,    
            alignItems: 'center'
          }}>
          <TextAndLabelView
            label={t("opportunitySize")}
            labelSize="xs"
            heading={currencyFormatter.format(value)}
            contentSize="md"/>
        </div>

        <div 
          style={{
            padding: '0 20px',
            textAlign: 'center',
            blockSize: '80px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            borderInlineEnd: `1px solid ${SYSTEM_COLORS.GRAY.SPANISH_GRAY}`,
            alignItems: 'default'
          }}>
          {reactivateOpportunity ? (
            <TextAndLabelView label={t("status")} labelSize="xs" text={statusText} textColor="orange" />
          ) : (
            <TextAndLabelView label={t("deletion")} labelSize="xs" text={removedDateText} />
          )}
        </div>

        <div
          style={{
            padding: '0 20px', textAlign: 'center', blockSize: '80px', display: 'flex',
            flexDirection: 'column', justifyContent: 'center', borderInlineEnd: "none", alignItems: 'default' }}>
          {reactivateOpportunity ? (
            <ButtonWithModal
              ctx={{
                label: `${t("actions")}...`,
                icon: "",
                modalWidth: "650px",
                onOpen: resetModalState,
                renderModalContent: renderActionModal
              }}
            />
          ) : (
            <ButtonWithModal
              ctx={{
                label: t("reopen"),
                modalHeading: t("wishReopenOpportunity"),
                icon: "",
                modalWidth: "650px",
                onOpen: resetModalState,
                renderModalContent: renderReactivateModal
              }}
            />
          )}
        </div>
      </div>
      
      {accordionButton && (
        <div style={{ position: 'absolute', bottom: '-10px', left: '41%' }}>
          <Button onClick={handleAccordionButton} label="">
            <Icon size="16" icon={openAccordion === type1 ? "chevron-up" : "chevron-down"} />
          </Button>
        </div>
      )}

      {openAccordion && (
        <CardOutlined>
          <CardSection
            ctx={{ ...baseCardSectionCtx, ...cardSectionCtx }}
            actions={{ ...cardSectionActions }}
            uiState={{ accordionMode, ...cardSectionUiState }}
          />
        </CardOutlined>
      )}
    </Card>
  );
};

ListItem.CardSection = OpportunityAccordion

ListItem.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};

ActionModal.propTypes = { actions: PropTypes.shape(), ctx: PropTypes.shape() };